import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Download, Loader2, Upload, XCircle } from "lucide-react";

import { useModalContext } from "../../components/ModalProvider";
import { ApplicationError, UnauthorizedError } from "../../general/Errors";
import {
  AnalysisEvent,
  AnalysisRow,
  AnalysisStep,
  ConversionPayload,
  DataRoom,
  ExtractionPayload,
  RolePayload,
  SubtypePayload,
  useDocumentAnalysisAPI,
} from "../../DocumentAnalysisAPI";
import ResultCard from "./ResultCard";
import StepRow, { StepState } from "./StepRow";
import { StatusBadge } from "./analysisView";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

/**
 * The processing test: drop a document, watch what the pipeline makes of it.
 *
 * The reason this is a tab and not a script is the middle of the run. When a classification comes
 * out wrong the question is never only "which role did it say" — it is whether the conversion
 * produced readable text, what the model was told, and what it thought the alternatives were. All
 * of that is on screen here, step by step, while it happens.
 *
 * Nothing is persisted. A run exists as the events it emitted, and the result can be taken away as
 * JSON. Persisting findings — attaching a classification to a document in the archive — is the next
 * thing this would grow into, and the shape of the payloads is already the shape that would be
 * stored; it is deliberately not built.
 */

/** How often a dropped connection is resumed before the run is called lost. */
const MAX_RECONNECTS = 5;

const STEPS: Array<Exclude<AnalysisStep, "job">> = [
  "conversion",
  "roleClassification",
  "subtypeClassification",
  "objectExtraction",
  "matching",
];

interface RunState {
  jobId: string | null;
  fileName: string;
  steps: Record<string, StepState>;
  /** Whether the run is over — either way; the difference is in the steps. */
  finished: boolean;
  error: string | null;
  /** The stored record of this run, once the server says which one it is. */
  analysisId: string | null;
}

const emptySteps = (): Record<string, StepState> => ({
  conversion: { status: "waiting" },
  roleClassification: { status: "waiting" },
  subtypeClassification: { status: "waiting" },
  objectExtraction: { status: "waiting" },
  matching: { status: "waiting" },
});

const NO_DATA_ROOM = "__none__";

interface Properties {
  /** Hand a finished run over to the tab that lists them. */
  onOpenAnalysis?: (analysisId: string) => void;
}

export default function ProcessingTestTab({ onOpenAnalysis }: Properties) {
  const { t } = useTranslation();
  const { pushError } = useModalContext() as any;
  const api = useDocumentAnalysisAPI();
  const [searchParams, setSearchParams] = useSearchParams();

  const [run, setRun] = useState<RunState | null>(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [dataRooms, setDataRooms] = useState<DataRoom[]>([]);
  const [dataRoomId, setDataRoomId] = useState<string>(NO_DATA_ROOM);
  const [recent, setRecent] = useState<AnalysisRow[]>([]);

  const fileInput = useRef<HTMLInputElement | null>(null);
  const abortController = useRef<AbortController | null>(null);
  /** What the browser has already seen, so a reconnect can resume rather than start over. */
  const lastEventId = useRef(0);
  /**
   * Whether the run said it was over.
   *
   * A ref rather than the state below, because the reconnect loop has to read it between two
   * awaits — and only the envelope event means "there will be nothing more". A stream that simply
   * ends is a dropped connection, whatever it looked like from here.
   */
  const sawEnvelope = useRef(false);

  const apply = useCallback(
    (event: AnalysisEvent) => {
      if (event.step === "job") {
        sawEnvelope.current = true;
        setRun((current) =>
          current
            ? {
                ...current,
                finished: true,
                error: event.status === "error" ? (event.message ?? "Fehlgeschlagen.") : null,
                // The envelope carries the record's id — the run said where it was written down.
                analysisId: event.payload?.analysisId ?? current.analysisId,
              }
            : current,
        );

        setSearchParams(
          (current) => {
            const next = new URLSearchParams(current);
            next.delete("jobId");
            return next;
          },
          { replace: true },
        );
        return;
      }

      setRun((current) => {
        if (!current) return current;

        const previous = current.steps[event.step] ?? { status: "waiting" };

        // `progress` events only refine what a running step is doing; they must not overwrite a
        // payload a `completed` already delivered.
        const status: StepState["status"] =
          event.status === "completed"
            ? "done"
            : event.status === "failed"
              ? "failed"
              : "running";

        return {
          ...current,
          steps: {
            ...current.steps,
            [event.step]: {
              status,
              message: event.message ?? previous.message,
              progress: event.progress ?? previous.progress,
              elapsedMs: event.elapsedMs,
              payload: event.payload ?? previous.payload,
            },
          },
        };
      });
    },
    [setSearchParams],
  );

  /**
   * Follow a run until it ends.
   *
   * Reconnects on its own when the stream breaks mid-run: a dropped connection is not the end of
   * the run, and `Last-Event-ID` means resuming costs nothing but the round trip. It gives up when
   * the run is over or the user cancelled.
   */
  const follow = useCallback(
    async (jobId: string) => {
      for (let attempt = 0; attempt <= MAX_RECONNECTS; attempt++) {
        const controller = new AbortController();
        abortController.current = controller;

        try {
          await api.streamAnalysisJob(
            jobId,
            lastEventId.current,
            (event, id) => {
              if (id > 0) lastEventId.current = id;
              apply(event);
            },
            controller.signal,
          );

          // The stream ended. Only the envelope makes that the end of the *run* — anything else is
          // a connection that went away quietly, and resuming costs one round trip.
          if (sawEnvelope.current) return;

          await new Promise((resolve) => setTimeout(resolve, 1500));
          continue;
        } catch (error) {
          if (controller.signal.aborted) return;

          // A refused stream is not a broken connection: retrying would loop until the tab is
          // closed. Only a connection that dropped is worth resuming.
          if (error instanceof UnauthorizedError || error instanceof ApplicationError) {
            setRun((current) =>
              current
                ? { ...current, finished: true, error: (error as Error).message || String(error) }
                : current,
            );
            return;
          }

          await new Promise((resolve) => setTimeout(resolve, 1500));
        }
      }

      // The connection kept dropping. Better to say so than to leave a stepper that never moves.
      setRun((current) =>
        current ? { ...current, finished: true, error: t("documentAnalysisPanel.test.lost") } : current,
      );

      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.delete("jobId");
          return next;
        },
        { replace: true },
      );
    },
    [apply, setSearchParams, t],
  );

  const start = async (file: File) => {
    abortController.current?.abort();
    lastEventId.current = 0;
    sawEnvelope.current = false;
    setUploading(true);

    setRun({
      jobId: null,
      fileName: file.name,
      steps: emptySteps(),
      finished: false,
      error: null,
      analysisId: null,
    });

    try {
      const { jobId, analysisId } = await api.startAnalysisJob(
        file,
        dataRoomId === NO_DATA_ROOM ? null : dataRoomId,
      );
      setRun((current) => (current ? { ...current, jobId, analysisId } : current));

      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.set("jobId", jobId);
          return next;
        },
        { replace: true },
      );

      void follow(jobId);
    } catch (error) {
      // A refused upload never became a run: there is nothing to watch, and a stepper showing a
      // failed first step would suggest the file was tried.
      setRun(null);
      pushError(error);
    } finally {
      setUploading(false);
    }
  };

  const cancel = async () => {
    const jobId = run?.jobId;
    abortController.current?.abort();

    if (!jobId) {
      setRun(null);
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.delete("jobId");
          return next;
        },
        { replace: true },
      );
      return;
    }

    try {
      await api.abortAnalysisJob(jobId);
    } catch (error) {
      pushError(error);
    }

    setRun((current) =>
      current ? { ...current, finished: true, error: t("documentAnalysisPanel.test.cancelled") } : current,
    );

    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete("jobId");
        return next;
      },
      { replace: true },
    );
  };

  useEffect(() => () => abortController.current?.abort(), []);

  useEffect(() => {
    const jobId = searchParams.get("jobId");
    if (jobId && !run) {
      setRun({
        jobId,
        // Replaced by the first replayed conversion event, which carries the name.
        fileName: t("documentAnalysisPanel.test.resuming"),
        steps: emptySteps(),
        finished: false,
        error: null,
        analysisId: null,
      });
      void follow(jobId);
    }
  }, [searchParams, run, follow]);

  const loadRecent = useCallback(async () => {
    try {
      const result = await api.getAnalyses({ limit: 5, sort: "startedAt", direction: "desc" });
      setRecent(result.analyses ?? []);
    } catch {
      // A convenience list. Its absence must not make the tab look broken.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const result = await api.getDataRooms();
        setDataRooms(result.dataRooms ?? []);
      } catch {
        // Same: without data rooms the dropdown simply does not appear.
      }
    };

    void load();
    void loadRecent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A finished run belongs at the top of the list, and only a finished one does.
  useEffect(() => {
    if (run?.finished) void loadRecent();
  }, [run?.finished, loadRecent]);

  const download = () => {
    if (!run) return;

    const result = {
      file: run.fileName,
      conversion: run.steps.conversion?.payload ?? null,
      role: run.steps.roleClassification?.payload ?? null,
      subtype: run.steps.subtypeClassification?.payload ?? null,
      // In full: the extracted objects are the part of a run worth keeping, and the download is the
      // only way to keep one.
      extraction: run.steps.objectExtraction?.payload ?? null,
      matching: run.steps.matching?.payload ?? null,
    };

    const url = URL.createObjectURL(
      new Blob([JSON.stringify(result, null, 2)], { type: "application/json" }),
    );
    const link = document.createElement("a");
    link.href = url;
    link.download = `${run.fileName.replace(/\.[^.]+$/, "")}-analyse.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const running = !!run && !run.finished;

  const role = run?.steps.roleClassification?.payload as RolePayload | undefined;
  const subtype = run?.steps.subtypeClassification?.payload as SubtypePayload | undefined;
  const conversion = run?.steps.conversion?.payload as ConversionPayload | undefined;
  const extraction = run?.steps.objectExtraction?.payload as ExtractionPayload | undefined;

  return (
    <div className="flex min-w-0 flex-col gap-6">
      {/* Only where there is something to choose: a dropdown with one entry saying "none" is a
          question nobody asked. */}
      {dataRooms.length > 0 ? (
        <div className="w-[260px]">
          <Label htmlFor="testDataRoom">{t("documentAnalysisPanel.analyses.dataRoom")}</Label>
          <Select value={dataRoomId} onValueChange={setDataRoomId}>
            <SelectTrigger id="testDataRoom">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_DATA_ROOM}>{t("documentAnalysisPanel.analyses.noDataRoom")}</SelectItem>
              {dataRooms.map((room) => (
                <SelectItem key={room._id} value={room._id}>
                  {room.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : null}

      <div
        className={`flex flex-col items-center gap-2 rounded-lg border-2 border-dashed p-8 text-center transition-colors ${
          dragging ? "border-primary bg-primary/5" : "border-muted-foreground/30"
        }`}
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);

          const file = event.dataTransfer.files?.[0];
          if (file && !running) void start(file);
        }}
      >
        <Upload className="h-8 w-8 text-muted-foreground" aria-hidden />
        <p className="text-sm font-medium">{t("documentAnalysisPanel.test.dropzone")}</p>
        <p className="max-w-prose text-xs text-muted-foreground">
          {t("documentAnalysisPanel.test.formats")}
        </p>

        <input
          ref={fileInput}
          type="file"
          className="hidden"
          accept=".pdf,.docx,.txt,.csv,.xlsx,.pptx,.html,.md,.jpg,.jpeg,.png"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) void start(file);
            // Cleared, so dropping the same file twice in a row starts a second run.
            event.target.value = "";
          }}
        />

        <Button
          variant="outline"
          className="mt-2"
          disabled={running || uploading}
          onClick={() => fileInput.current?.click()}
        >
          {uploading ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
          ) : (
            <Upload className="mr-2 h-4 w-4" aria-hidden />
          )}
          {t("documentAnalysisPanel.test.choose")}
        </Button>
      </div>

      {run ? (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-semibold">{run.fileName}</h3>

            <div className="flex items-center gap-2">
              {running ? (
                <Button variant="outline" size="sm" onClick={cancel}>
                  {t("documentAnalysisPanel.test.cancel")}
                </Button>
              ) : null}

              {run.finished && !run.error ? (
                <Button variant="outline" size="sm" onClick={download}>
                  <Download className="mr-2 h-4 w-4" aria-hidden />
                  {t("documentAnalysisPanel.test.download")}
                </Button>
              ) : null}
            </div>
          </div>

          <ol className="flex flex-col gap-2">
            {STEPS.map((step) => (
              <StepRow key={step} step={step} state={run.steps[step] ?? { status: "waiting" }} />
            ))}
          </ol>

          {run.error ? (
            <div className="flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
              <XCircle className="h-4 w-4 shrink-0" aria-hidden />
              {run.error}
            </div>
          ) : null}

          {run.finished && !run.error ? (
            <ResultCard role={role} subtype={subtype} conversion={conversion} extraction={extraction} />
          ) : null}

          {/* Said once the run is over, whichever way it went: a failed run is recorded too, and
              that record is the one worth looking at. */}
          {run.finished && run.analysisId ? (
            <p className="text-xs text-muted-foreground">
              {t("documentAnalysisPanel.analyses.saved")}
              {onOpenAnalysis ? (
                <Button
                  variant="link"
                  size="sm"
                  className="h-auto px-1 py-0 text-xs"
                  onClick={() => onOpenAnalysis(run.analysisId as string)}
                >
                  {t("documentAnalysisPanel.analyses.openInAnalyses")}
                </Button>
              ) : null}
            </p>
          ) : null}
        </div>
      ) : null}

      {recent.length > 0 ? (
        <div className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold">{t("documentAnalysisPanel.analyses.recent")}</h3>

          <ul className="flex flex-col gap-1">
            {recent.map((entry) => (
              <li key={entry._id} className="flex flex-wrap items-center gap-2 text-xs">
                <span className="text-muted-foreground">
                  {new Date(entry.startedAt).toLocaleString("de-DE", {
                    dateStyle: "short",
                    timeStyle: "short",
                  })}
                </span>
                <button
                  type="button"
                  className="max-w-[280px] truncate hover:underline"
                  title={entry.document?.fileName}
                  onClick={() => onOpenAnalysis?.(entry._id)}
                >
                  {entry.document?.fileName}
                </button>
                <span>{entry.roleName ?? "—"}</span>
                <StatusBadge status={entry.status} failedStep={entry.failedStep} />
                <span className="font-mono text-muted-foreground">{entry.extractedCount ?? 0}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
